import { useFormContext } from 'react-hook-form'
import i18next from 'i18next'
import { FormInputProps } from '@microservices/wiskey-react-components'
import { RadioOption } from '@microservices/wiskey-react-components/dist/hookFormControllers/RadioGroupController'

import { FormInputsType } from '@pages/FormCreateOrEdit/types'

import { GENERATOR_INPUT_TYPE } from '@constants'
import { SelectOption } from '@types'
import { ConstFormat } from '@formats'

import {
  BindTypeJs,
  BindTypeStatic,
  BindTypeStaticBoolean,
  GanttPlanningMode,
  GridLineParams,
  GuideLines,
  UNIT_INTERVAL_OPTIONS,
} from '@gantt/components/GanttCreateOrEdit'

import { useHandlers } from './useHandlers'

//todo move to src/pages/GanttChart/components/GanttCreateOrEdit/types  и сопутствующее на тот же уровень
export type GanttParametersFormType = {
  axisX: {
    hasToolbar: boolean
    planning?: {
      enabled: boolean
      mode?: GanttPlanningMode
    }
    isTimeWindow: boolean
    hasRealtimeLine: boolean
    realtimeLineWidth: string
    extraVirtualizationCells: string
    realtimeLineColor: BindTypeStatic
    pastBackgroundColor: BindTypeStatic
    futureBackgroundColor: BindTypeStatic
    unitInterval: UNIT_INTERVAL_OPTIONS
    defaultScale?: number
    lines: {
      top: {
        has: BindTypeStaticBoolean | BindTypeJs
        color: BindTypeStatic | BindTypeJs
        width: BindTypeStatic | BindTypeJs
      }
      bottom: {
        has: BindTypeStaticBoolean | BindTypeJs
        color: BindTypeStatic | BindTypeJs
        width: BindTypeStatic | BindTypeJs
      }
    }
    borders: {
      inner: {
        has: BindTypeStaticBoolean | BindTypeJs
        color: BindTypeStatic | BindTypeJs
        width: BindTypeStatic | BindTypeJs
      }
      outer: {
        has: BindTypeStaticBoolean | BindTypeJs
        color: BindTypeStatic | BindTypeJs
        width: BindTypeStatic | BindTypeJs
      }
    }
    activeSegment: {
      has: BindTypeStaticBoolean | BindTypeJs
      color: BindTypeStatic | BindTypeJs
      opacity: string
      verticalMargin: BindTypeStatic | BindTypeJs
      sideMargin: BindTypeStatic | BindTypeJs
    }
    textColor: BindTypeStatic | BindTypeJs
    boxVerticalGuideLines: GuideLines
    cursorVerticalGuideLine: Omit<GuideLines, 'isBoxColor'>
  }
  axisY: {
    sortable: boolean
    extraVirtualizationRows: string
  }
  backgroundGridAppearance: {
    primaryLine: GridLineParams
    secondaryLine: GridLineParams
    horizontalLine: GridLineParams
  }
  hasBacklog: boolean
  rowHeight: string
  backlogHeight: string
  yAxisWidth: string
  xAxisWidth: string
  noRealTime: boolean
  hasRowExpansion: boolean
  isResizableY: boolean
  isResizableBacklog: boolean
  rowsExpansionAtStart: boolean
}

const planningModeOptions: RadioOption[] = [
  {
    value: GanttPlanningMode.ROW,
    label: i18next.t('ganttCreate.generalForm.labels.planningViewAllTasks'),
  },
  {
    value: GanttPlanningMode.GROUPS,
    label: i18next.t('ganttCreate.generalForm.labels.planningViewByGroups'),
  },
]

export const useGanttParametersForm = (
  unitIntervalSelectOptions?: SelectOption[],
  defaultValues?: GanttParametersFormType
) => {
  const methods = useFormContext()
  const { reset, watch } = methods
  const watchHasBacklog = watch('canvas.hasBacklog')
  const watchHasPlanning = watch('canvas.axisX.planning.enabled')

  const handleReset = () => {
    reset(defaultValues)
  }
  const { state, handlers } = useHandlers({
    onCloseParameters: handleReset,
    onOpenParameters: handleReset,
  })

  const generalInputs: FormInputsType[] = [
    {
      name: 'canvas.hasBacklog',
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: i18next.t('ganttCreate.generalForm.labels.hasBacklog'),
      labelPlacement: 'end',
      labelSx: {
        minWidth: 'auto',
      },
      formLabelSx: { width: '90px' },
    },
    ...(watchHasBacklog
      ? [
          {
            name: 'canvas.isResizableBacklog',
            inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
            label: i18next.t('ganttCreate.generalForm.labels.isResizableBacklog'),
            labelPlacement: 'end',
            labelSx: { width: '100%' },
          } as FormInputProps,
        ]
      : []),
    ...(watchHasBacklog
      ? [
          {
            name: 'canvas.backlogHeight',
            inputType: GENERATOR_INPUT_TYPE.INPUT,
            placeholder: i18next.t('ganttCreate.generalForm.placeholders.backlogHeight'),
            label: i18next.t('ganttCreate.generalForm.labels.backlogHeight'),
            textFieldInputProps: {
              inputComponent: ConstFormat as any,
              inputProps: { max: 500 },
            },
          },
        ]
      : []),
    // {
    //   name: 'noRealTime',
    //   inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
    //   label: i18next.t('ganttCreate.generalForm.labels.noRealTime'),
    //   labelPlacement: 'end',
    // },
  ]
  const axisXInputs: FormInputsType[] = [
    {
      name: 'canvas.axisX.hasToolbar',
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: i18next.t('ganttCreate.generalForm.labels.hasToolbar'),
      labelPlacement: 'end',
      labelSx: {
        minWidth: 'auto',
      },
      formLabelSx: { width: '145px' },
    },
    {
      name: 'canvas.axisX.unitInterval',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      placeholder: i18next.t('ganttCreate.generalForm.placeholders.unitInterval'),
      label: i18next.t('ganttCreate.generalForm.labels.unitInterval'),
      selectOptions: unitIntervalSelectOptions,
    },
    {
      name: 'canvas.axisX.defaultScale',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: i18next.t('ganttCreate.generalForm.placeholders.defaultScale'),
      label: i18next.t('ganttCreate.generalForm.labels.defaultScale'),
      textFieldInputProps: {
        inputComponent: ConstFormat as any,
        inputProps: { min: 1, max: 16 },
      },
    },
    // {
    //   name: 'axisX.extraVirtualizationCells',
    //   inputType: GENERATOR_INPUT_TYPE.INPUT,
    //   placeholder: i18next.t('ganttCreate.generalForm.placeholders.extraVirtualizationCells'),
    //   type: 'number',
    //   label: i18next.t('ganttCreate.generalForm.labels.extraVirtualizationCells'),
    // },
  ]

  const axisYInputs: FormInputsType[] = [
    {
      name: 'canvas.axisY.sortable',
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: i18next.t('ganttCreate.generalForm.labels.sortable'),
      labelPlacement: 'end',
      labelSx: {
        minWidth: 'auto',
      },
      formLabelSx: { width: '120px' },
    },
    // {
    //   name: 'axisY.extraVirtualizationRows',
    //   inputType: GENERATOR_INPUT_TYPE.INPUT,
    //   placeholder: i18next.t('ganttCreate.generalForm.placeholders.extraVirtualizationRows'),
    //   type: 'number',
    //   label: i18next.t('ganttCreate.generalForm.labels.extraVirtualizationRows'),
    //   labelSx: {
    //     minWidth: 200,
    //   },
    // },
  ]

  const planningInputs: FormInputsType[] = [
    {
      name: 'canvas.axisX.planning.enabled',
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: i18next.t('ganttCreate.generalForm.labels.planningEnabled'),
      labelPlacement: 'end',
      labelSx: { minWidth: 'auto' },
      formLabelSx: { width: '245px' },
    },
    ...(watchHasPlanning
      ? [
          {
            name: 'canvas.axisX.planning.mode',
            inputType: GENERATOR_INPUT_TYPE.RADIO_GROUP,
            labelSx: { flex: 1, width: 200 },
            label: i18next.t('ganttCreate.generalForm.labels.planningMode'),
            radioOptionsList: planningModeOptions,
            formInputContainerSx: {
              '.MuiFormGroup-root': { width: 560 },
              width: 'fit-content',
            },
          },
        ]
      : []),
  ]

  return {
    methods,
    state: {
      generalInputs,
      axisXInputs,
      axisYInputs,
      planningInputs,
      ...state,
    },
    handlers: {
      ...handlers,
    },
  }
}
